import {
  Add as AddIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Alert, Box, Button, Paper, Snackbar, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  OrderStatusDetailModal,
  OrderStatusFiltersPanel,
  OrderStatusFormModal,
  OrderStatusStatisticsCards,
  OrderStatusTable,
} from "../../components/backend/orderstatus";
import {
  useOptimisticOrderStatusUpdate,
  useOrderStatuses,
  useOrderStatusMutations,
  useOrderStatusStatistics,
} from "../../hooks/useOrderStatus";
import {
  BulkUpdateOrderStatusRequest,
  CreateOrderStatusRequest,
  OrderStatus,
  OrderStatusFilters,
  OrderStatusModalMode,
  OrderStatusModalState,
  UpdateOrderStatusRequest,
} from "../../types/admin";

import AdminPage from "./AdminPage";

const OrderStatusPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters and pagination
  const [filters, setFilters] = useState<OrderStatusFilters>({
    pageNumber: 1,
    pageSize: 20,
    sortBy: "sortOrder",
    sortDescending: false,
  });

  // State for selected statuses
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  // State for modals
  const [modalState, setModalState] = useState<OrderStatusModalState>({
    open: false,
    mode: "view",
  });

  // State for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // React Query hooks
  const {
    data: statusesData,
    isLoading,
    error: statusesError,
    refetch: refetchStatuses,
  } = useOrderStatuses(filters);

  const {
    data: statisticsData,
    isLoading: statisticsLoading,
    error: statisticsError,
    refetch: refetchStatistics,
  } = useOrderStatusStatistics();

  // Extract data from React Query responses
  const statuses = statusesData?.data?.statuses || [];
  const totalCount = statusesData?.data?.totalCount || 0;
  const statistics = statisticsData?.data || null;

  // Order status mutations
  const mutations = useOrderStatusMutations();
  const { updateOrderStatusOptimistically } = useOptimisticOrderStatusUpdate();

  // Utility functions
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Modal handlers
  const openModal = (mode: OrderStatusModalMode, status?: OrderStatus) => {
    setModalState({ open: true, mode, status });
  };

  const closeModal = () => {
    setModalState({ open: false, mode: "view" });
  };

  // CRUD handlers
  const handleCreateStatus = async (request: CreateOrderStatusRequest) => {
    try {
      const newStatus = await mutations.createStatus.mutateAsync(request);
      showSnackbar(t("admin.orderStatus.created"));
      closeModal();
      return newStatus;
    } catch (error) {
      console.error("Error creating order status:", error);
      showSnackbar(t("admin.orderStatus.createFailed"), "error");
      throw error;
    }
  };

  const handleUpdateStatus = async (
    id: number,
    request: UpdateOrderStatusRequest,
  ) => {
    try {
      const updatedStatus = await mutations.updateStatus.mutateAsync({
        id,
        request,
      });
      showSnackbar(t("admin.orderStatus.updated"));
      closeModal();
      return updatedStatus;
    } catch (error) {
      console.error("Error updating order status:", error);
      showSnackbar(t("admin.orderStatus.updateFailed"), "error");
      throw error;
    }
  };

  const handleDeleteStatus = async (id: number) => {
    try {
      await mutations.deleteStatus.mutateAsync(id);
      showSnackbar(t("admin.orderStatus.deleted"));
    } catch (error) {
      console.error("Error deleting order status:", error);
      showSnackbar(t("admin.orderStatus.deleteFailed"), "error");
      throw error;
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      // Optimistic update
      const status = statuses.find((s: OrderStatus) => s.id === id);
      if (status) {
        updateOrderStatusOptimistically(id, { isActive: !status.isActive });
      }

      await mutations.toggleStatusActive.mutateAsync(id);
      showSnackbar(
        status?.isActive
          ? t("admin.orderStatus.deactivated")
          : t("admin.orderStatus.activated"),
      );
    } catch (error) {
      console.error("Error toggling order status:", error);
      showSnackbar(t("admin.orderStatus.toggleFailed"), "error");
      throw error;
    }
  };

  const handleBulkToggleActive = async (
    statusIds: number[],
    isActive: boolean,
  ) => {
    try {
      const request: BulkUpdateOrderStatusRequest = {
        statusIds,
        isActive,
      };
      await mutations.bulkUpdateStatusActive.mutateAsync(request);
      showSnackbar(
        isActive
          ? t("admin.orderStatus.bulkActivated", { count: statusIds.length })
          : t("admin.orderStatus.bulkDeactivated", { count: statusIds.length }),
      );
      setSelectedStatuses([]);
    } catch (error) {
      console.error("Error bulk updating order statuses:", error);
      showSnackbar(t("admin.orderStatus.bulkUpdateFailed"), "error");
      throw error;
    }
  };

  const handleExport = async () => {
    try {
      // TODO: Implement export functionality
      showSnackbar(t("admin.orderStatus.exportComingSoon"), "info");
    } catch (error) {
      console.error("Error exporting order statuses:", error);
      showSnackbar(t("admin.orderStatus.exportFailed"), "error");
    }
  };

  const handleRefresh = () => {
    refetchStatuses();
    refetchStatistics();
    showSnackbar(t("admin.orderStatus.refreshed"));
  };

  // Error handling
  if (statusesError) {
    return (
      <AdminPage>
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("admin.orderStatus.loadFailed")}
          </Alert>
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
          >
            {t("common.retry")}
          </Button>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4">{t("admin.orderStatus.title")}</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              disabled={isLoading}
              startIcon={<RefreshIcon />}
            >
              {t("admin.orderStatus.refresh")}
            </Button>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={isLoading}
              startIcon={<DownloadIcon />}
            >
              {t("admin.orderStatus.export")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openModal("create")}
            >
              {t("admin.orderStatus.addNewStatus")}
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <OrderStatusStatisticsCards
            statistics={statistics}
            loading={statisticsLoading}
          />
        )}

        {/* Filters */}
        <OrderStatusFiltersPanel
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExport}
          loading={isLoading}
        />

        {/* Order Statuses Table */}
        <Paper>
          <OrderStatusTable
            statuses={statuses}
            totalCount={totalCount}
            filters={filters}
            onFiltersChange={setFilters}
            onView={(status) => openModal("view", status)}
            onEdit={(status) => openModal("edit", status)}
            onDelete={handleDeleteStatus}
            onToggleActive={handleToggleActive}
            onBulkToggleActive={handleBulkToggleActive}
            loading={isLoading}
            selectedStatuses={selectedStatuses}
            onSelectionChange={setSelectedStatuses}
          />
        </Paper>

        {/* Modals */}
        <OrderStatusFormModal
          open={
            modalState.open &&
            (modalState.mode === "create" || modalState.mode === "edit")
          }
          mode={modalState.mode}
          status={modalState.status}
          allStatuses={statuses}
          onClose={closeModal}
          onSuccess={async (request) => {
            if (modalState.mode === "create") {
              await handleCreateStatus(request);
            } else if (modalState.mode === "edit" && modalState.status) {
              await handleUpdateStatus(modalState.status.id, request);
            }
          }}
        />

        <OrderStatusDetailModal
          open={modalState.open && modalState.mode === "view"}
          status={modalState.status || null}
          allStatuses={statuses}
          onClose={closeModal}
          onEdit={(status) =>
            setModalState({ open: true, mode: "edit", status })
          }
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default OrderStatusPage;
